import type { TrainingExerciseDto, TrainingPlanDto } from '@ukinfo/shared';

/**
 * Speicher fuer das, was ein Trainer am Platz ohne Netz braucht.
 *
 * Die Einheiten liegen in IndexedDB (keine zusaetzliche Bibliothek), je
 * angemeldetem Konto getrennt: jeder Schluessel beginnt mit der Kennung des
 * Zugangs. Videos sind eigene, grosse Datensaetze (Blob). Alles andere im
 * Browser - Cache Storage, localStorage - bleibt frei von diesen Daten.
 *
 * Die Oberflaeche spricht nur mit der Schnittstelle `OfflineStore`. Die
 * Tests setzen mit `setOfflineStore` einen Speicher im Arbeitsspeicher ein.
 */

export interface OfflineEntry {
  /** `<userId>|<planId>`. */
  key: string;
  userId: string;
  planId: string;
  /** Einheit eines Trainings oder Vorlage. */
  kind: 'session' | 'template';
  sessionId: string | null;
  /** Titel des Trainings (bzw. der Vorlage). */
  title: string;
  startAt: string | null;
  endAt: string | null;
  location: string | null;
  teamName: string | null;
  /** ISO-Zeitpunkt des Speicherns bzw. der letzten Aktualisierung. */
  savedAt: string;
  /** Vom automatischen Abgleich angelegt (wird nach dem Training wieder aufgeraeumt). */
  auto: boolean;
  plan: TrainingPlanDto;
  /** Uebungen der Eintraege in voller Form (Texte, Skizze, Videoliste), nach ID. */
  exercises: Record<string, TrainingExerciseDto>;
  /** Kennungen der Videos, die tatsaechlich auf dem Geraet liegen. */
  videoIds: string[];
  /** Ungefaehre Groesse der Texte (ohne Videos). */
  bytes: number;
}

export interface OfflineVideo {
  /** `<userId>|<videoId>`. */
  key: string;
  userId: string;
  videoId: string;
  mimeType: string;
  size: number;
  blob: Blob;
}

export interface OfflineStore {
  listEntries(userId: string): Promise<OfflineEntry[]>;
  getEntry(userId: string, planId: string): Promise<OfflineEntry | undefined>;
  putEntry(entry: OfflineEntry): Promise<void>;
  deleteEntry(userId: string, planId: string): Promise<void>;
  listVideos(userId: string): Promise<OfflineVideo[]>;
  getVideo(userId: string, videoId: string): Promise<OfflineVideo | undefined>;
  putVideo(video: OfflineVideo): Promise<void>;
  deleteVideo(userId: string, videoId: string): Promise<void>;
  /** Alles eines Kontos entfernen (Einheiten und Videos). */
  clearUser(userId: string): Promise<void>;
  /** Alle Einheiten und Videos entfernen (die Merkzettel in "meta" bleiben). */
  clearAll(): Promise<void>;
  /** Alles ausser den Daten des genannten Kontos entfernen. */
  clearOthers(userId: string): Promise<void>;
  getMeta(key: string): Promise<string | undefined>;
  setMeta(key: string, value: string): Promise<void>;
  deleteMeta(key: string): Promise<void>;
}

export const entryKey = (userId: string, planId: string): string => `${userId}|${planId}`;
export const videoKey = (userId: string, videoId: string): string => `${userId}|${videoId}`;

/* ------------------------------------------------------------------ *
 * Speicher im Arbeitsspeicher (Tests, Rueckfall)
 * ------------------------------------------------------------------ */

export function createMemoryStore(): OfflineStore {
  const entries = new Map<string, OfflineEntry>();
  const videos = new Map<string, OfflineVideo>();
  const meta = new Map<string, string>();
  // Wie bei IndexedDB: gespeichert wird eine Kopie, nicht das Objekt der Oberflaeche.
  const copy = <T>(value: T): T => structuredClone(value);
  return {
    listEntries: async (userId) => [...entries.values()].filter((entry) => entry.userId === userId).map(copy),
    getEntry: async (userId, planId) => {
      const entry = entries.get(entryKey(userId, planId));
      return entry ? copy(entry) : undefined;
    },
    putEntry: async (entry) => { entries.set(entry.key, copy(entry)); },
    deleteEntry: async (userId, planId) => { entries.delete(entryKey(userId, planId)); },
    listVideos: async (userId) => [...videos.values()].filter((video) => video.userId === userId),
    getVideo: async (userId, videoId) => videos.get(videoKey(userId, videoId)),
    putVideo: async (video) => { videos.set(video.key, video); },
    deleteVideo: async (userId, videoId) => { videos.delete(videoKey(userId, videoId)); },
    clearUser: async (userId) => {
      for (const [key, entry] of entries) if (entry.userId === userId) entries.delete(key);
      for (const [key, video] of videos) if (video.userId === userId) videos.delete(key);
    },
    clearAll: async () => { entries.clear(); videos.clear(); },
    clearOthers: async (userId) => {
      for (const [key, entry] of entries) if (entry.userId !== userId) entries.delete(key);
      for (const [key, video] of videos) if (video.userId !== userId) videos.delete(key);
    },
    getMeta: async (key) => meta.get(key),
    setMeta: async (key, value) => { meta.set(key, value); },
    deleteMeta: async (key) => { meta.delete(key); },
  };
}

/* ------------------------------------------------------------------ *
 * IndexedDB
 * ------------------------------------------------------------------ */

const DB_NAME = 'ukinfo-offline';
const DB_VERSION = 1;
const ENTRIES = 'entries';
const VIDEOS = 'videos';
const META = 'meta';

function wrap<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB-Fehler'));
  });
}

function done(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB-Fehler'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Speichern abgebrochen'));
  });
}

export function createIdbStore(factory: IDBFactory = indexedDB): OfflineStore {
  let opened: Promise<IDBDatabase> | null = null;
  const open = (): Promise<IDBDatabase> => {
    opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        const entriesStore = db.createObjectStore(ENTRIES, { keyPath: 'key' });
        entriesStore.createIndex('userId', 'userId');
        const videosStore = db.createObjectStore(VIDEOS, { keyPath: 'key' });
        videosStore.createIndex('userId', 'userId');
        db.createObjectStore(META);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => { opened = null; reject(request.error ?? new Error('IndexedDB nicht verfügbar')); };
      request.onblocked = () => { opened = null; reject(new Error('IndexedDB ist blockiert')); };
    });
    return opened;
  };

  const tx = async (stores: string[], mode: IDBTransactionMode): Promise<IDBTransaction> => (await open()).transaction(stores, mode);
  const byUser = async <T>(store: string, userId: string): Promise<T[]> => {
    const transaction = await tx([store], 'readonly');
    return wrap<T[]>(transaction.objectStore(store).index('userId').getAll(userId));
  };
  const remove = async (store: string, key: string): Promise<void> => {
    const transaction = await tx([store], 'readwrite');
    transaction.objectStore(store).delete(key);
    await done(transaction);
  };
  /** Loescht alle Datensaetze, deren Konto `matches` zutrifft. */
  const sweep = async (matches: (userId: string) => boolean): Promise<void> => {
    const transaction = await tx([ENTRIES, VIDEOS], 'readwrite');
    for (const name of [ENTRIES, VIDEOS]) {
      const request = transaction.objectStore(name).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return;
        if (matches((cursor.value as { userId: string }).userId)) cursor.delete();
        cursor.continue();
      };
    }
    await done(transaction);
  };

  return {
    listEntries: (userId) => byUser<OfflineEntry>(ENTRIES, userId),
    getEntry: async (userId, planId) => {
      const transaction = await tx([ENTRIES], 'readonly');
      return wrap<OfflineEntry | undefined>(transaction.objectStore(ENTRIES).get(entryKey(userId, planId)));
    },
    putEntry: async (entry) => {
      const transaction = await tx([ENTRIES], 'readwrite');
      transaction.objectStore(ENTRIES).put(entry);
      await done(transaction);
    },
    deleteEntry: (userId, planId) => remove(ENTRIES, entryKey(userId, planId)),
    listVideos: (userId) => byUser<OfflineVideo>(VIDEOS, userId),
    getVideo: async (userId, videoId) => {
      const transaction = await tx([VIDEOS], 'readonly');
      return wrap<OfflineVideo | undefined>(transaction.objectStore(VIDEOS).get(videoKey(userId, videoId)));
    },
    putVideo: async (video) => {
      const transaction = await tx([VIDEOS], 'readwrite');
      transaction.objectStore(VIDEOS).put(video);
      await done(transaction);
    },
    deleteVideo: (userId, videoId) => remove(VIDEOS, videoKey(userId, videoId)),
    clearUser: (userId) => sweep((id) => id === userId),
    clearAll: () => sweep(() => true),
    clearOthers: (userId) => sweep((id) => id !== userId),
    getMeta: async (key) => {
      const transaction = await tx([META], 'readonly');
      return wrap<string | undefined>(transaction.objectStore(META).get(key));
    },
    setMeta: async (key, value) => {
      const transaction = await tx([META], 'readwrite');
      transaction.objectStore(META).put(value, key);
      await done(transaction);
    },
    deleteMeta: async (key) => {
      const transaction = await tx([META], 'readwrite');
      transaction.objectStore(META).delete(key);
      await done(transaction);
    },
  };
}

/* ------------------------------------------------------------------ *
 * Der Speicher der Anwendung
 * ------------------------------------------------------------------ */

let current: OfflineStore | null = null;

/** Fuer Tests: eigenen Speicher einsetzen (null = zurueck zu IndexedDB). */
export function setOfflineStore(store: OfflineStore | null): void {
  current = store;
}

/** Gibt es hier einen Speicher? Ohne IndexedDB (alte Browser, manche privaten Fenster) fehlen die Knoepfe. */
export function offlineSupported(): boolean {
  return current !== null || (typeof indexedDB !== 'undefined' && indexedDB !== null);
}

export function offlineStore(): OfflineStore {
  if (current) return current;
  if (typeof indexedDB === 'undefined') throw new Error('Dieser Browser kann nichts offline speichern');
  current = createIdbStore();
  return current;
}
